import React, { useEffect, useRef, useState } from 'react'

// Icon Components
const BoltIcon = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
)

const EyeIcon = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
)

const HeartIcon = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
    />
  </svg>
)

const ClipboardIcon = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
    />
  </svg>
)

const CheckIcon = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
)

const WhoWeAre = () => {
  const [isInView, setIsInView] = useState(false)
  const [activeTab, setActiveTab] = useState('mission')
  const [isMobile, setIsMobile] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)

  // Detect screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
        }
      },
      { threshold: 0.1 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  // Tab content data
  const tabs = [
    {
      id: 'mission',
      label: 'Mission',
      icon: BoltIcon,
      title: 'Our Mission',
      content:
        "To bring calm, clarity, and functionality to homes and workspaces through customized organizing solutions that reflect our clients' needs and lifestyles.",
      tagline: 'Transforming chaos into calm',
      color: 'warm', // warm sand
      highlights: ['Calm', 'Clarity', 'Functionality'],
    },
    {
      id: 'vision',
      label: 'Vision',
      icon: EyeIcon,
      title: 'Our Vision',
      content:
        'To become the most trusted name in professional organization, helping clients discover the transformative power of beautifully organized spaces.',
      tagline: 'Creating lasting change',
      color: 'cool', // lavender mist
      highlights: ['Trusted', 'Transformative', 'Beautiful'],
    },
    {
      id: 'values',
      label: 'Values',
      icon: HeartIcon,
      title: 'Our Values',
      content: null,
      values: [
        { text: 'Personalized attention to every detail', icon: '✨' },
        { text: 'Sustainable, long-lasting solutions', icon: '🌿' },
        { text: 'Respect, trust, and confidentiality', icon: '🤝' },
        { text: 'Exceeding expectations every time', icon: '⭐' },
      ],
      tagline: 'What guides us',
      color: 'sage', // soft sage
    },
    {
      id: 'process',
      label: 'Process',
      icon: ClipboardIcon,
      title: 'Our Process',
      content:
        'Every transformation begins with understanding. We listen, assess, and create a customized plan that turns your vision into reality. From sorting to styling, we handle every detail with care.',
      tagline: 'Thoughtful and thorough',
      color: 'plum', // dusty plum
      steps: ['Consult', 'Plan', 'Sort', 'Organize', 'Style', 'Maintain'],
    },
  ]

  const activeTabData = tabs.find((tab) => tab.id === activeTab) || tabs[0]

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      const currentIndex = tabs.findIndex((t) => t.id === activeTab)
      if (currentIndex < tabs.length - 1) {
        setActiveTab(tabs[currentIndex + 1].id)
      }
    }
    if (isRightSwipe) {
      const currentIndex = tabs.findIndex((t) => t.id === activeTab)
      if (currentIndex > 0) {
        setActiveTab(tabs[currentIndex - 1].id)
      }
    }
  }

  // Get color classes based on tab
  const getColorClasses = (color: string) => {
    switch (color) {
      case 'warm':
        return 'bg-[#D5B8A8]/10 text-[#D5B8A8]'
      case 'cool':
        return 'bg-[#B0A0B9]/10 text-[#B0A0B9]'
      case 'sage':
        return 'bg-[#A8B8A3]/10 text-[#A8B8A3]'
      case 'plum':
        return 'bg-[#806F82]/10 text-[#806F82]'
      default:
        return 'bg-[#596856]/10 text-[#596856]'
    }
  }

  const getBorderColor = (color: string) => {
    switch (color) {
      case 'warm':
        return 'border-[#D5B8A8]'
      case 'cool':
        return 'border-[#B0A0B9]'
      case 'sage':
        return 'border-[#A8B8A3]'
      case 'plum':
        return 'border-[#806F82]'
      default:
        return 'border-[#596856]'
    }
  }

  return (
    <section
      ref={sectionRef}
      id="who-we-are"
      className="py-12 sm:py-16 md:py-20 lg:py-24 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #F4F1ED 0%, #FFFFFF 50%, #F4F1ED 100%)',
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className={`absolute top-10 right-10 sm:right-1/4 w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 rounded-full opacity-[0.02] bg-[#596856] transition-all duration-1000 ${
            isInView ? 'scale-100' : 'scale-0'
          }`}
        />
        <div
          className={`absolute bottom-10 left-10 sm:left-1/4 w-24 sm:w-32 md:w-48 h-24 sm:h-32 md:h-48 rounded-full opacity-[0.02] bg-[#A8B8A3] transition-all duration-1000 delay-300 ${
            isInView ? 'scale-100' : 'scale-0'
          }`}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative max-w-7xl">
        {/* HEADER - Responsive typography */}
        <div
          className={`text-center mb-8 sm:mb-10 md:mb-12 transition-all duration-1000 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/80 backdrop-blur-sm rounded-full border border-[#A8B8A3]/20 shadow-sm mb-4 sm:mb-6">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#D5B8A8] rounded-full animate-pulse" />
            <span className="text-[#596856] text-xs sm:text-sm font-medium tracking-wide uppercase">
              About Us
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-[#596856] mb-2 sm:mb-3">
            Who We Are
          </h2>
          <p className="text-sm sm:text-base text-[#596856]/60 max-w-2xl mx-auto px-4">
            Professional organizing with a personal touch
          </p>
        </div>

        {/* Mobile-optimized tab navigation */}
        <div
          className={`transition-all duration-1000 delay-200 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {/* Tab buttons - responsive sizing */}
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-6 sm:mb-8 px-2 sm:px-0">
            {tabs.map((tab, index) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-[#596856] text-white shadow-md scale-105'
                      : 'bg-white/80 text-[#596856] hover:bg-white border border-[#596856]/10'
                  }`}
                  style={{
                    animationDelay: `${index * 100}ms`,
                  }}
                >
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{tab.label}</span>
                  </div>
                </button>
              )
            })}
          </div>

          {/* RESPONSIVE CONTENT LAYOUT */}
          <div className="relative max-w-6xl mx-auto">
            {isMobile ? (
              // Mobile Layout - Stacked cards
              <div
                className="space-y-4"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {/* Company Card - Always visible on mobile */}
                <div
                  className={`transition-all duration-700 delay-300 ${
                    isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-[#596856]/5 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-serif text-[#596856]">
                          Sorted & Organized by CM
                        </h3>
                        <p className="text-xs text-[#596856]/60 italic mt-1">
                          Serving Minneapolis & Beyond
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D5B8A8]/20 to-[#B0A0B9]/20 flex items-center justify-center">
                        <span className="text-lg">✨</span>
                      </div>
                    </div>

                    <p className="text-sm text-[#596856]/80 leading-relaxed mb-4">
                      We transform cluttered spaces into calm, functional environments that bring
                      joy and efficiency to your daily life.
                    </p>

                    {/* Mobile stats grid */}
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#596856]/5">
                      <div className="text-center">
                        <div className="text-base font-semibold text-[#B0A0B9]">100+</div>
                        <div className="text-[10px] text-[#596856]/60">Happy Clients</div>
                      </div>
                      <div className="text-center">
                        <div className="text-base font-semibold text-[#D5B8A8]">10+</div>
                        <div className="text-[10px] text-[#596856]/60">Years</div>
                      </div>
                      <div className="text-center">
                        <div className="text-base font-semibold text-[#A8B8A3]">5★</div>
                        <div className="text-[10px] text-[#596856]/60">Rating</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Active Tab Content - Mobile */}
                <div
                  className={`transition-all duration-500 ${
                    isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  <div
                    className={`bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border-2 ${getBorderColor(
                      activeTabData.color
                    )} p-5`}
                  >
                    {/* Tab header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${getColorClasses(activeTabData.color)}`}>
                        {React.createElement(activeTabData.icon, { className: 'w-5 h-5' })}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-serif text-[#596856]">{activeTabData.title}</h3>
                        <p className="text-xs text-[#596856]/50 italic">{activeTabData.tagline}</p>
                      </div>
                    </div>

                    {/* Tab content */}
                    <div className="space-y-3">
                      {activeTabData.id === 'values' && activeTabData.values ? (
                        <ul className="space-y-2">
                          {activeTabData.values.map((value, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-base mt-0.5">{value.icon}</span>
                              <span className="text-sm text-[#596856]/80 flex-1">{value.text}</span>
                            </li>
                          ))}
                        </ul>
                      ) : activeTabData.id === 'process' && activeTabData.steps ? (
                        <div>
                          <p className="text-sm text-[#596856]/80 leading-relaxed mb-3">
                            {activeTabData.content}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {activeTabData.steps.map((step, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-[#596856]/5 text-xs text-[#596856]/70 rounded-full"
                              >
                                {index + 1}. {step}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-[#596856]/80 leading-relaxed">
                            {activeTabData.content}
                          </p>
                          {activeTabData.highlights && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {activeTabData.highlights.map((highlight, index) => (
                                <span
                                  key={index}
                                  className={`px-2 py-0.5 text-xs rounded-full ${getColorClasses(
                                    activeTabData.color
                                  )}`}
                                >
                                  {highlight}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Swipe indicator for mobile */}
                    <div className="mt-4 pt-3 border-t border-[#596856]/5">
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          {tabs.map((_, i) => (
                            <div
                              key={i}
                              className={`w-6 h-0.5 rounded-full transition-all ${
                                i === tabs.findIndex((t) => t.id === activeTab)
                                  ? 'bg-[#596856] w-8'
                                  : 'bg-[#596856]/10'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-[10px] text-[#596856]/40 flex items-center gap-1">
                          <span>Swipe to explore</span>
                          <span>→</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Desktop/Tablet Layout - Side by side
              <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                {/* Left side - Company intro */}
                <div
                  className={`transition-all duration-700 delay-300 ${
                    isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}
                >
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-[#596856]/5 p-6 lg:p-8 h-full">
                    <div className="mb-6">
                      <div className="w-16 h-0.5 bg-gradient-to-r from-[#D5B8A8] to-[#B0A0B9] mb-4"></div>
                      <h3 className="text-2xl font-serif text-[#596856] mb-2">
                        Sorted & Organized by CM
                      </h3>
                      <p className="text-sm text-[#596856]/60 italic">
                        Professional organizing with a personal touch
                      </p>
                    </div>

                    <p className="text-base text-[#596856]/80 leading-relaxed mb-6">
                      We're a Minneapolis-based professional organizing service specializing in
                      transforming cluttered spaces into calm, functional environments. From busy
                      family homes to corporate offices, we create custom solutions that work with
                      your lifestyle.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-3">
                        <CheckIcon className="w-5 h-5 text-[#A8B8A3]" />
                        <span className="text-sm text-[#596856]/80">Family-owned & operated</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckIcon className="w-5 h-5 text-[#A8B8A3]" />
                        <span className="text-sm text-[#596856]/80">10+ years of experience</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckIcon className="w-5 h-5 text-[#A8B8A3]" />
                        <span className="text-sm text-[#596856]/80">
                          100% satisfaction guaranteed
                        </span>
                      </div>
                    </div>

                    {/* Desktop stats */}
                    <div className="grid grid-cols-3 gap-4 pt-6 border-t border-[#596856]/5">
                      <div className="text-center">
                        <div className="text-2xl font-semibold text-[#B0A0B9]">100+</div>
                        <div className="text-xs text-[#596856]/60">Spaces Organized</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-semibold text-[#D5B8A8]">10+</div>
                        <div className="text-xs text-[#596856]/60">Years Experience</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-semibold text-[#A8B8A3]">100%</div>
                        <div className="text-xs text-[#596856]/60">Satisfaction</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right side - Tab content */}
                <div className="relative h-full min-h-[400px]">
                  {tabs.map((tab) => {
                    const Icon = tab.icon
                    return (
                      <div
                        key={tab.id}
                        className={`absolute inset-0 transition-all duration-500 ${
                          activeTab === tab.id
                            ? 'opacity-100 translate-x-0 pointer-events-auto'
                            : 'opacity-0 translate-x-4 pointer-events-none'
                        }`}
                      >
                        <div
                          className={`bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border-2 ${getBorderColor(
                            tab.color
                          )} p-6 lg:p-8 h-full flex flex-col`}
                        >
                          {/* Tab header */}
                          <div className="flex items-center gap-4 mb-6">
                            <div className={`p-3 rounded-xl ${getColorClasses(tab.color)}`}>
                              <Icon className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-serif text-[#596856]">{tab.title}</h3>
                              <p className="text-sm text-[#596856]/50 italic">{tab.tagline}</p>
                            </div>
                          </div>

                          {/* Tab content */}
                          <div className="flex-grow">
                            {tab.id === 'values' && tab.values ? (
                              <ul className="space-y-3">
                                {tab.values.map((value, index) => (
                                  <li key={index} className="flex items-start gap-3">
                                    <span className="text-xl mt-0.5">{value.icon}</span>
                                    <span className="text-base text-[#596856]/80">
                                      {value.text}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : tab.id === 'process' && tab.steps ? (
                              <div>
                                <p className="text-base text-[#596856]/80 leading-relaxed mb-6">
                                  {tab.content}
                                </p>
                                <div className="grid grid-cols-3 gap-3">
                                  {tab.steps.map((step, index) => (
                                    <div
                                      key={index}
                                      className="text-center p-3 bg-[#596856]/5 rounded-lg"
                                    >
                                      <div className="text-lg font-semibold text-[#596856]/30 mb-1">
                                        {index + 1}
                                      </div>
                                      <div className="text-sm text-[#596856]/70">{step}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="text-base text-[#596856]/80 leading-relaxed mb-6">
                                  {tab.content}
                                </p>
                                {tab.highlights && (
                                  <div className="flex flex-wrap gap-3">
                                    {tab.highlights.map((highlight, index) => (
                                      <span
                                        key={index}
                                        className={`px-4 py-2 text-sm rounded-full ${getColorClasses(
                                          tab.color
                                        )}`}
                                      >
                                        {highlight}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Progress indicator */}
                          <div className="mt-6 pt-6 border-t border-[#596856]/5">
                            <div className="flex items-center justify-between">
                              <div className="flex gap-2">
                                {tabs.map((_, i) => (
                                  <div
                                    key={i}
                                    className={`w-10 h-1 rounded-full transition-all ${
                                      i === tabs.findIndex((t) => t.id === activeTab)
                                        ? 'bg-[#596856] w-12'
                                        : 'bg-[#596856]/10'
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm text-[#596856]/40">
                                {tabs.findIndex((t) => t.id === activeTab) + 1} / {tabs.length}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default WhoWeAre
